import { html, nothing, unsafeCSS } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, state } from 'lit/decorators.js';

import style from './credit-quality.scss?inline';
import { PopApiBaseComponent } from '../../base/pop-api-base-component';
import { Constants, VIEWS } from './credit-quality.constants';
import type {
  CreditQualityValues,
  CreditQualityContentObject,
} from './credit-quality.model';
import {
  dateShort,
  dateStringSplit,
  dateToMMDDYYYY,
} from '../../common/utils/formatDate';
import { generateLocalDisclaimerText } from '../../common/utils/generateDisclaimer';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';
import { isNullish } from '../../common/utils/nullish';
import { CreditQualityFragments } from '../../common/fragments/credit-quality.fragments';

/**
 * @tag pop-credit-quality-component
 * @attribute { CreditQualityContentObject } [data-component-object] CreditQuality content object
 */
@customElement(Constants.TAG_NAME)
export class PopCreditQualityComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * CreditQuality content object
   */
  componentObject?: CreditQualityContentObject;

  /**
   * @internal
   */
  fragment = CreditQualityFragments.creditQualityFragment;

  /**
   * @internal
   */
  @state()
  view: string = Constants.DEFAULT_VIEW;

  @state()
  selectedAsOfDate: string | undefined = '';

  constructor() {
    super(new ApiService());
  }

  protected processResponse(_response): void {
    this.componentObject = _response?.creditQualityExhibit;
  }

  protected initialize(): void {
    super.initialize();

    this.selectedAsOfDate = this.componentObject?.asOfDate;
  }

  private renderTopSection() {
    return html`
      <h3
        class="credit-quality-heading b-margin-block-end--micro--normal b-typography-serif--headline--100 b-font-weight-bold"
      >
        ${Constants.COMPONENT_TITLE}<span
          data-disclaimer-code="${Constants.COMPONENT_DISCLAIMER_CODE}"
        ></span>
      </h3>
    `;
  }

  private renderControlSection() {
    return html`
      <div class="credit-quality-controls b-margin-block-start--micro--jumbo">
        ${this.renderAsOfDate()} ${this.renderViewDropdown()}
      </div>
    `;
  }

  private renderAsOfDate() {
    const availableDates = this.componentObject?.availablePriorDates || [];

    return html`
      ${availableDates && availableDates.length === 1
        ? html` <p
            class="b-typography-sans-serif--caption-loose--300 b-font-weight-light"
          >
            ${Constants.DATA_AS_OF_DROPDOWN_LABEL}
            ${dateShort(availableDates[0])}
          </p>`
        : nothing}
      ${availableDates && availableDates.length > 1
        ? html`<beacon-control>
            <label slot="label" for="${Constants.DATA_AS_OF_DROPDOWN_ID}">
              ${Constants.DATA_AS_OF_DROPDOWN_LABEL}
            </label>
            <select
              id="${Constants.DATA_AS_OF_DROPDOWN_ID}"
              @change="${this.asOfDateChangeHandler}"
            >
              ${availableDates.map(
                (date) =>
                  html`<option
                    value="${date}"
                    ?selected=${date === this.selectedAsOfDate}
                  >
                    ${dateToMMDDYYYY(date)}
                  </option>`,
              )}
            </select>
          </beacon-control> `
        : nothing}
    `;
  }

  private asOfDateChangeHandler(
    event: Event & { target?: { value?: string } },
  ) {
    if (event.target?.value) {
      this.selectedAsOfDate = dateStringSplit(event.target?.value);
      this.refreshData();
    }
  }

  private refreshData(): void {
    this.callApiService({
      historicalDates: {
        creditQuality: [this.selectedAsOfDate],
      },
    });
  }

  private renderViewDropdown() {
    return html` <beacon-control>
      <label slot="label" for="${Constants.VIEW_DROPDOWN_ID}"
        >${Constants.VIEW_DROPDOWN_LABEL}</label
      >
      <select
        id="${Constants.VIEW_DROPDOWN_ID}"
        @change="${this.viewChangeHandler}"
      >
        ${Object.values(VIEWS).map((view) => {
          return html`
            <option value="${view}" ?selected=${view === this.view}>
              ${view}
            </option>
          `;
        })}
      </select>
    </beacon-control>`;
  }

  private viewChangeHandler(event: Event & { target?: { value?: string } }) {
    const value = event.target?.value;
    if (value) {
      this.view = value;
    } else {
      console.warn(`Invalid view value: ${value}`);
    }
  }

  private renderContentSection() {
    return html`
      ${this.view === VIEWS.TABLE
        ? this.renderTableView()
        : this.renderChartView()}
      ${this.renderBenchmarkName()}
    `;
  }

  private renderTableView() {
    const itemList = this.componentObject?.values || [];

    if (!itemList || itemList.length === 0) {
      return html`<pop-no-data-available-component
        .message="${Constants.CREDIT_QUALITY_UNAVAILABLE}"
      ></pop-no-data-available-component>`;
    }

    // Sort the itemList based on rank
    const sortedItemList = this.sortItemListByRank(itemList);

    // The keyed directive is being used to wipe out the table and start from scratch
    // when the as of date changes. This is necessary due to how the sorting in the Beacon
    // table modifies the DOM, causing the table to error when new data needs to be rendered.
    // Note: This will also cause the sort state of the table to be reset.
    return html`
      ${keyed(
        this.selectedAsOfDate,
        html` <div class="b-margin-block-start--micro--jumbo">
          <pop-credit-quality-table-component
            data-component-object="${JSON.stringify({
              itemList: sortedItemList,
            })}"
          ></pop-credit-quality-table-component>
        </div>`,
      )}
    `;
  }

  private renderChartView() {
    const itemList = this.componentObject?.values || [];

    if (!itemList || itemList.length === 0) {
      return html`<pop-no-data-available-component
        .message="${Constants.CREDIT_QUALITY_UNAVAILABLE}"
      ></pop-no-data-available-component>`;
    }

    // Sort the itemList based on rank
    const sortedItemList = this.sortItemListByRank(itemList);

    return html`<div
      class="credit-quality-chart b-margin-block-start--micro--jumbo"
    >
      <pop-credit-quality-chart-component
        data-component-object="${JSON.stringify({
          itemList: sortedItemList,
        })}"
      ></pop-credit-quality-chart-component>
    </div>`;
  }

  /**
   * Sort the itemList based on rank
   * @param itemList - The list of items to be sorted
   * @returns - The sorted list of items
   * */
  private sortItemListByRank(
    itemList: CreditQualityValues[],
  ): CreditQualityValues[] {
    return itemList
      .filter((item) => !isNullish(item.rank))
      .sort((a, b) => a.rank - b.rank);
  }

  private renderDisclaimerSection() {
    const disclaimers = this.componentObject?.disclaimers;
    if (!disclaimers || disclaimers.length === 0) {
      return html``;
    }

    const disclaimerText = generateLocalDisclaimerText(disclaimers);

    return html`<div class="b-margin-block-start--micro--jumbo">
      <pop-disclosure-component data-footnote-indicator-type="symbol">
        ${unsafeHTML(sanitizeHTML(disclaimerText))}
      </pop-disclosure-component>
    </div>`;
  }

  renderBenchmarkName() {
    if (this.componentObject?.benchmarkName) {
      return html`<p class="credit-quality-benchmark">
        <span
          class="b-typography-sans-serif--caption-loose--300 b-font-weight-bold"
          >${Constants.BENCHMARK_PREFIX}</span
        >
        <span
          class="b-typography-sans-serif--caption-loose--300 b-font-weight-light"
          >${this.componentObject?.benchmarkName}</span
        >
      </p>`;
    }

    return nothing;
  }

  render() {
    return this.renderComponent(html`
      <section class="credit-quality-content b-container">
        ${this.renderTopSection()}
        ${this.componentObject?.values?.length
          ? html` ${this.renderControlSection()} ${this.renderContentSection()}
            ${this.renderDisclaimerSection()}`
          : html` <pop-no-data-available-component
              .message="${Constants.CREDIT_QUALITY_UNAVAILABLE}"
            ></pop-no-data-available-component>`}
      </section>
    `);
  }
}
